/**
 * auto-gift.js
 * Ajoute automatiquement un ou plusieurs produits cadeaux au panier quand
 * leur seuil est atteint. Chaque cadeau est indépendant.
 *
 * Config (#auto-gift-config) :
 *   { "enabled": true, "gifts": [{ "threshold": 55, "variantId": 123 }, ...] }
 *   Ancien format accepté : { "enabled": true, "threshold": 55, "variantId": 123 }
 *
 * Règles (par cadeau) :
 * - Ajout automatique quand otherTotal >= seuil ET cadeau absent ET non refusé
 * - Retrait automatique (1 unité) quand otherTotal < seuil ET script avait ajouté
 * - Si l'utilisateur supprime le cadeau PENDANT une session au-dessus du seuil :
 *     → ne pas le réattribuer tant qu'il ne passe pas sous le seuil puis le repasse
 * - Ne jamais supprimer une unité ajoutée manuellement depuis la fiche produit
 * - otherTotal exclut tous les produits cadeaux configurés
 * - La ligne cadeau porte la propriété cachée _auto_gift : elle est reconnue
 *   côté panier (tous appareils, même sans localStorage) et retirée dès que
 *   le panier repasse sous le seuil. Le cadeau ne reste jamais seul.
 * - Un cadeau qui n'est PAS réellement à 0 € est retiré du panier : Shopify
 *   n'applique qu'une seule remise produit par commande, donc un code promo
 *   (ex. un acheté un offert) annule la remise qui rend le cadeau gratuit.
 *   Sans ce filet, le client paierait un article annoncé comme offert.
 */
(function () {
  'use strict';

  const STORE_KEY = 'autogift_v1';

  function getConfig() {
    try {
      const el = document.getElementById('auto-gift-config');
      if (!el) return null;
      const c = JSON.parse(el.textContent);
      if (!c.enabled) return null;
      let gifts = Array.isArray(c.gifts) ? c.gifts : [];
      if (!gifts.length && c.variantId && c.threshold > 0) {
        gifts = [{ threshold: c.threshold, variantId: c.variantId }];
      }
      gifts = gifts.filter(g => g && g.variantId && g.threshold > 0);
      if (!gifts.length) return null;
      gifts.sort((a, b) => a.threshold - b.threshold);
      return { gifts };
    } catch (_) { return null; }
  }

  // État : { [variantId]: { q: scriptQty, rm: userRemovedInSession } }
  function loadState() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); }
    catch (_) { return {}; }
  }
  function saveState(s) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (_) {}
  }

  const GIFT_PROP = '_auto_gift';

  function isGiftLine(it) {
    return Boolean(it && it.properties && it.properties[GIFT_PROP]);
  }

  // ── Filet : ne jamais laisser au panier un cadeau qui serait facturé ───────
  // Mémoire du blocage : { [variantId]: empreinte du panier } . Tant que le
  // panier et les codes promo ne bougent pas, on ne retente pas d'ajouter le
  // cadeau (sinon ajout/retrait en boucle).
  const NOFREE_KEY = 'autogift_nofree_v1';

  function loadNoFree() {
    try { return JSON.parse(localStorage.getItem(NOFREE_KEY) || '{}'); }
    catch (_) { return {}; }
  }
  function saveNoFree(o) {
    try { localStorage.setItem(NOFREE_KEY, JSON.stringify(o)); } catch (_) {}
  }

  // Empreinte du blocage. Avec un code promo appliqué, elle ne dépend que du
  // code : le cadeau ne redeviendra pas gratuit en changeant le panier, inutile
  // de le remettre puis l'enlever à chaque modification. Sans code, on retient
  // la composition du panier, pour réessayer dès qu'elle bouge.
  function blockKey(cart, giftVids) {
    const codes = (cart.discount_codes || [])
      .filter(c => c && c.applicable !== false)
      .map(c => c.code).sort().join(',');
    if (codes) return 'code:' + codes;
    const lines = (cart.items || [])
      .filter(it => !giftVids.includes(String(it.variant_id)))
      .map(it => it.variant_id + 'x' + it.quantity).sort().join('|');
    return 'panier:' + lines;
  }

  function paidGiftLines(cart) {
    return (cart.items || []).filter(it => isGiftLine(it) && it.final_line_price > 0);
  }

  // Retire les lignes cadeau facturées. Retourne le panier à jour.
  async function dropPaidGifts(cart, giftVids, flags) {
    const key = blockKey(cart, giftVids);
    if (!paidGiftLines(cart).length) {
      // Plus rien de facturé : on oublie les blocages devenus obsolètes
      const nf = loadNoFree();
      let changed = false;
      Object.keys(nf).forEach(vid => { if (nf[vid] !== key) { delete nf[vid]; changed = true; } });
      if (changed) saveNoFree(nf);
      return cart;
    }
    // Seconde lecture : la remise automatique peut arriver avec un temps de retard
    await new Promise(r => setTimeout(r, 700));
    const fresh = await refetchCart(cart);
    const paid = paidGiftLines(fresh);
    if (!paid.length) return fresh;

    const updates = {};
    const nf = loadNoFree();
    const st = loadState();
    const freshKey = blockKey(fresh, giftVids);
    paid.forEach(it => {
      updates[it.key] = 0;
      nf[String(it.variant_id)] = freshKey;
      delete st[String(it.variant_id)];
    });
    saveNoFree(nf);
    saveState(st);
    if (await updateLines(updates)) {
      flags.mutated = true;
      return await refetchCart(fresh);
    }
    return fresh;
  }

  let _busy = false;
  let _pending = false;

  async function evaluate() {
    if (_busy) { _pending = true; return; }
    const cfg = getConfig();
    if (!cfg) return;
    _busy = true;
    _pending = false;
    try {
      const resp = await fetch('/cart.js');
      if (!resp.ok) return;
      await process(await resp.json(), cfg);
    } catch (_) {}
    finally {
      _busy = false;
      if (_pending) setTimeout(evaluate, 50);
    }
  }

  async function process(cart, cfg) {
    const giftVids = cfg.gifts.map(g => String(g.variantId));
    const flags = { mutated: false };
    let lastAddedVid = null;

    // Avant tout : aucun cadeau facturé ne reste au panier
    cart = await dropPaidGifts(cart, giftVids, flags);
    let mutated = flags.mutated;

    for (const gift of cfg.gifts) {
      const vid = String(gift.variantId);
      const thresholdCents = Math.round(gift.threshold * 100);
      const state = loadState();
      const s = state[vid] || {};
      const scriptQty = Number(s.q || 0);
      const userRemoved = Boolean(s.rm || false);

      const lines = cart.items || [];
      // Lignes cadeau marquées (ajoutées par ce script)
      const giftLines = lines.filter(it => String(it.variant_id) === vid && isGiftLine(it));
      const giftQty = giftLines.reduce((n, it) => n + it.quantity, 0);
      // Ancienne ligne sans marqueur, ajoutée par la version précédente du script
      // (état local sans le drapeau p). Un Cahier acheté n'est jamais concerné.
      const legacyLine = scriptQty > 0 && !s.p && giftQty === 0
        ? lines.find(it => String(it.variant_id) === vid && !isGiftLine(it))
        : null;

      // Total hors produits cadeaux (tous cadeaux configurés exclus)
      const otherTotal = lines
        .filter(it => !giftVids.includes(String(it.variant_id)))
        .reduce((sum, it) => sum + it.line_price, 0);

      if (otherTotal >= thresholdCents) {
        // ── AU-DESSUS DU SEUIL ─────────────────────────────────────────────

        // Le cadeau ajouté par le script vient d'être supprimé par l'utilisateur
        if (scriptQty > 0 && giftQty === 0 && !legacyLine) {
          saveState({ ...state, [vid]: { q: 0, rm: true } });
          continue;
        }

        // Un seul exemplaire offert
        if (giftQty > 1) {
          const updates = {};
          giftLines.forEach((it, i) => { updates[it.key] = i === 0 ? 1 : 0; });
          if (await updateLines(updates)) { mutated = true; cart = await refetchCart(cart); }
          continue;
        }

        const blocked = loadNoFree()[vid] === blockKey(cart, giftVids);

        if (giftQty === 0 && !legacyLine && !userRemoved && !blocked) {
          if (await addGift(vid, state)) {
            mutated = true;
            lastAddedVid = vid;
            cart = await refetchCart(cart);
          }
        } else if (giftQty === 1 && scriptQty === 0) {
          saveState({ ...state, [vid]: { q: 1, rm: false, p: 1 } });
        }

      } else {
        // ── EN DESSOUS DU SEUIL ────────────────────────────────────────────

        const updates = {};
        giftLines.forEach(it => { updates[it.key] = 0; });
        if (legacyLine) updates[legacyLine.key] = Math.max(0, legacyLine.quantity - scriptQty);

        if (Object.keys(updates).length) {
          if (await updateLines(updates)) {
            mutated = true;
            cart = await refetchCart(cart);
          }
        }
        if (loadState()[vid] !== undefined) {
          // Nettoyer l'état → la prochaine montée au-dessus du seuil repart de zéro
          const updated = loadState();
          delete updated[vid];
          saveState(updated);
        }
      }
    }

    if (mutated) {
      await refreshUI();
      if (lastAddedVid) {
        launchConfetti();
        highlightGiftItem(lastAddedVid);
      }
    }
  }

  async function refetchCart(fallback) {
    try {
      const resp = await fetch('/cart.js');
      if (resp.ok) return await resp.json();
    } catch (_) {}
    return fallback;
  }

  async function addGift(vid, state) {
    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1, properties: { [GIFT_PROP]: '1' } }] })
    });
    if (!resp.ok) return false;
    saveState({ ...state, [vid]: { q: 1, rm: false, p: 1 } });
    return true;
  }

  async function updateLines(updates) {
    const resp = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return resp.ok;
  }

  async function refreshUI() {
    const miniCartEl = document.querySelector('mini-cart');
    const cartItemsEl = document.querySelector('cart-items');

    let sections = [];
    if (miniCartEl && typeof miniCartEl.getSectionsToRender === 'function') {
      sections = miniCartEl.getSectionsToRender().map(s => s.section || s.id).filter(Boolean);
    } else if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
      sections = cartItemsEl.getSectionsToRender().map(s => s.section).filter(Boolean);
    }
    if (!sections.length) return;

    const resp = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: {},
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      })
    });
    if (!resp.ok) return;
    const data = await resp.json();
    if (!data.sections) return;

    if (miniCartEl && typeof miniCartEl.renderContents === 'function') {
      miniCartEl.renderContents(data);
    } else if (cartItemsEl && typeof cartItemsEl.renderContents === 'function') {
      cartItemsEl.renderContents(data);
    }
  }

  // ── Animations ────────────────────────────────────────────────────────────

  function launchConfetti() {
    // Trouver la position de l'icône panier comme point d'origine
    const cartIcon = document.querySelector('[href*="/cart"] .icon, .header__icon--cart, [data-cart-icon], a[href="/cart"]');
    let originX = window.innerWidth * 0.85;
    let originY = 60;
    if (cartIcon) {
      const rect = cartIcon.getBoundingClientRect();
      originX = rect.left + rect.width / 2;
      originY = rect.top + rect.height / 2;
    }

    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:99999;';
    document.body.appendChild(canvas);
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d');

    const COLORS = ['#f9c74f','#f94144','#90be6d','#577590','#f3722c','#ffffff','#c77dff'];
    const SHAPES = ['rect','circle','ribbon'];
    const particles = Array.from({ length: 90 }, () => {
      const angle = (Math.random() * Math.PI * 2);
      const speed = Math.random() * 9 + 3;
      return {
        x: originX, y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 4,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        w: Math.random() * 10 + 5,
        h: Math.random() * 5 + 3,
        rot: Math.random() * 360,
        rotV: (Math.random() - 0.5) * 12,
        shape: SHAPES[Math.floor(Math.random() * SHAPES.length)]
      };
    });

    const DURATION = 2800;
    let start = null;

    (function frame(ts) {
      if (!start) start = ts;
      const t = ts - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.18;
        p.vx *= 0.99;
        p.rot += p.rotV;
        const alpha = Math.max(0, 1 - t / DURATION);
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot * Math.PI / 180);
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === 'ribbon') {
          ctx.beginPath();
          ctx.ellipse(0, 0, p.w / 2, p.h / 4, 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      });

      if (t < DURATION) { requestAnimationFrame(frame); }
      else { canvas.remove(); }
    })(performance.now());
  }

  function highlightGiftItem(vid) {
    setTimeout(() => {
      // Trouver la ligne cadeau dans le tiroir
      const item = document.querySelector(`[data-variant-id="${vid}"]`)
        || Array.from(document.querySelectorAll('.cart-item, [class*="cart-item"]'))
            .find(el => el.innerHTML.includes(String(vid)));
      if (!item) return;

      // Injecter les styles une seule fois
      if (!document.getElementById('auto-gift-anim')) {
        const style = document.createElement('style');
        style.id = 'auto-gift-anim';
        style.textContent = `
          @keyframes autogift-pulse {
            0%   { background-color: rgba(249,199,79,.22); box-shadow: 0 0 0 3px rgba(249,199,79,1); }
            65%  { background-color: rgba(249,199,79,.07); box-shadow: 0 0 0 3px rgba(249,199,79,.3); }
            100% { background-color: transparent;          box-shadow: 0 0 0 3px transparent; }
          }
          .auto-gift-highlight {
            animation: autogift-pulse 1.8s ease-out forwards !important;
            border-radius: 4px;
            position: relative;
            z-index: 1;
          }
        `;
        document.head.appendChild(style);
      }

      // Ajouter une marge de scroll au-dessus de l'item = header + 16px de respiration
      const header = document.querySelector('.mini-cart__header');
      const headerHeight = header ? header.offsetHeight : 0;
      item.style.scrollMarginTop = (headerHeight + 16) + 'px';
      item.scrollIntoView({ behavior: 'smooth', block: 'start' });

      item.classList.add('auto-gift-highlight');
      item.addEventListener('animationend', () => {
        item.classList.remove('auto-gift-highlight');
        item.style.scrollMarginTop = '';
      }, { once: true });
    }, 450);
  }

  // ── Déclencheurs ──────────────────────────────────────────────────────────

  // 1. MutationObserver sur les bulles compteur du panier (avec retry)
  //    → se déclenche après TOUT ajout/modification, quelle que soit la méthode
  function watchCartBubbles(attempt) {
    attempt = attempt || 0;
    const selectors = [
      '#cart-icon-bubble',
      '#mobile-cart-icon-bubble',
      '.cart-count-bubble',
      '[data-cart-count]'
    ];
    const targets = document.querySelectorAll(selectors.join(','));
    if (!targets.length) {
      // Les éléments n'existent pas encore — réessayer jusqu'à 15 fois
      if (attempt < 15) setTimeout(() => watchCartBubbles(attempt + 1), 600);
      return;
    }
    const obs = new MutationObserver(() => setTimeout(evaluate, 200));
    targets.forEach(el => obs.observe(el, { childList: true, subtree: true, characterData: true }));
  }

  // 2. Soumission d'un formulaire add-to-cart (fiche produit, quick-add)
  //    → déclenche evaluate ~1s après pour laisser le temps à l'appel AJAX
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (form && form.action && /\/cart\/add/.test(form.action)) {
      setTimeout(evaluate, 1000);
    }
  }, true);

  // 3. PubSub natif du thème (pubsub.js — disponible si chargé avant ce script)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    try { subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(evaluate, 300)); } catch (_) {}
  }

  // 4. Événement cart:updated (changements depuis le tiroir / CartItems)
  document.addEventListener('cart:updated', () => setTimeout(evaluate, 150));

  // 5. Wrapper fetch (capte les appels qui passent par window.fetch au moment du clic)
  const _nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
    const promise = _nativeFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update)/.test(url)) {
      promise.then(r => { if (r && r.ok) setTimeout(evaluate, 300); }).catch(() => {});
    }
    return promise;
  };

  // 6. Démarrage : évaluation initiale + MutationObserver + polling de secours
  function boot() {
    watchCartBubbles();
    setTimeout(evaluate, 800);
    // Polling toutes les 7s — filet absolu si aucun des déclencheurs ci-dessus ne fonctionne
    setInterval(evaluate, 7000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
